import { describeError, useApi } from '@classgo/app-kit';
import type { TeacherListItem } from '@classgo/shared';
import { Button, Card, EmptyState, Modal, Table } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { TeacherForm } from '../../forms/teacher-form';
import { useT } from '../../i18n';
import { recordQueries } from '../../records/api';
import { useTenant } from '../../tenant/tenant-provider';

/**
 * Teachers with their homeroom groups and subjects this year.
 */
export const TeachersScreen = () => {
  const t = useT();
  const api = useApi();
  const navigate = useNavigate();
  const { session } = useTenant();
  const [creating, setCreating] = useState(false);
  const canWrite = session.permissions.all || session.permissions.keys.includes('teachers:write');
  const teachers = useQuery(recordQueries.teachers(api));
  return (
    <Card
      title={t('teachers.title')}
      flush
      actions={
        canWrite ? (
          <Button onClick={() => setCreating(true)}>{t('teachers.new')}</Button>
        ) : undefined
      }
    >
      <Modal open={creating} onClose={() => setCreating(false)} title={t('teachers.new')}>
        {creating ? (
          <TeacherForm
            current={null}
            onDone={(id) => {
              setCreating(false);
              void teachers.refetch();
              void navigate({ to: '/docentes/$teacherId', params: { teacherId: id } });
            }}
          />
        ) : null}
      </Modal>
      {teachers.isError ? (
        <div className="p-6">
          <EmptyState
            title={t('entity.error.title')}
            description={describeError(t, teachers.error)}
            action={
              <Button variant="secondary" onClick={() => void teachers.refetch()}>
                {t('session.retry')}
              </Button>
            }
          />
        </div>
      ) : (
        <Table<TeacherListItem>
          caption={t('teachers.caption')}
          rows={teachers.data ?? []}
          rowKey={(row) => row.id}
          loading={teachers.isPending}
          onRowClick={(row) =>
            void navigate({ to: '/docentes/$teacherId', params: { teacherId: row.id } })
          }
          empty={
            <EmptyState title={t('teachers.empty.title')} description={t('teachers.empty.body')} />
          }
          columns={[
            {
              key: 'name',
              header: t('teachers.column.name'),
              sortable: true,
              sortValue: (row) => `${row.familyName ?? ''} ${row.givenName ?? ''}`,
              cell: (row) => (
                <div>
                  <p className="font-medium text-gray-900">
                    {row.familyName}, {row.givenName}
                  </p>
                  <p className="text-xs text-gray-500">{row.email}</p>
                </div>
              ),
            },
            {
              key: 'homeroom',
              header: t('teachers.column.homeroom'),
              cell: (row) => row.homeroomGroups.join(', ') || t('common.none'),
            },
            {
              key: 'subjects',
              header: t('teachers.column.subjects'),
              cell: (row) => row.subjects.join(', ') || t('common.none'),
            },
            {
              key: 'groups',
              header: t('teachers.column.groups'),
              align: 'right',
              cell: (row) => row.groupsCount,
            },
          ]}
        />
      )}
    </Card>
  );
};
